/**
 * Top-down chair centers just outside the table silhouette. Ported from the POS
 * client (features/dine-in/floor-plan/seatLayout.ts) so the admin builder shows
 * seats the same way the POS app does.
 *
 * `width`/`height` must be the table's real rendered size (canvas units).
 */

const MAX_VISIBLE_SEATS = 12;

export const getSeatPoses = (shape, width, height, capacity) => {
  const count = Math.min(Math.max(Math.round(capacity), 0), MAX_VISIBLE_SEATS);
  if (count <= 0 || width <= 0 || height <= 0) {
    return [];
  }

  const shortSide = Math.min(width, height);
  // Seat depth ≈ chair size; gap places the chair CENTER outside the edge.
  const seatDepth = Math.max(10, Math.min(18, shortSide * 0.15));
  const gap = seatDepth * 0.58 + 2;

  if (shape === 'bar' || shape === 'booth') {
    return seatsAlongEdge(count, width, height, 'bottom', gap);
  }
  if (shape === 'round') {
    return radialSeats(count, width, height, gap);
  }
  return rectSideSeats(count, width, height, gap);
};

const radialSeats = (count, width, height, gap) => {
  const cx = width / 2;
  const cy = height / 2;
  const rx = width / 2 + gap;
  const ry = height / 2 + gap;
  const start = -Math.PI / 2;
  return Array.from({ length: count }, (_, i) => {
    const angle = start + (Math.PI * 2 * i) / count;
    return {
      x: cx + Math.cos(angle) * rx,
      y: cy + Math.sin(angle) * ry,
      rotation: (angle * 180) / Math.PI + 90,
    };
  });
};

const seatsAlongEdge = (count, width, height, edge, gap) => {
  if (count <= 0) {
    return [];
  }
  const inset = count === 1 ? 0.5 : Math.min(0.28, 0.12 + 0.5 / count);
  const poses = [];
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0.5 : inset + ((1 - inset * 2) * i) / (count - 1);
    if (edge === 'bottom') {
      poses.push({ x: width * t, y: height + gap, rotation: 180 });
    } else if (edge === 'top') {
      poses.push({ x: width * t, y: -gap, rotation: 0 });
    } else if (edge === 'left') {
      poses.push({ x: -gap, y: height * t, rotation: -90 });
    } else {
      poses.push({ x: width + gap, y: height * t, rotation: 90 });
    }
  }
  return poses;
};

/** Put more seats on the longer sides so a tall/narrow table stays tidy. */
const distributeSides = (count, width, height) => {
  const landscape = width >= height;
  const order = landscape
    ? ['top', 'bottom', 'left', 'right']
    : ['left', 'right', 'top', 'bottom'];
  const sides = { top: 0, right: 0, bottom: 0, left: 0 };
  for (let i = 0; i < count; i += 1) {
    sides[order[i % 4]] += 1;
  }
  return sides;
};

const rectSideSeats = (count, width, height, gap) => {
  if (count === 1) {
    return seatsAlongEdge(1, width, height, 'bottom', gap);
  }
  if (count === 2) {
    return [
      ...seatsAlongEdge(1, width, height, 'top', gap),
      ...seatsAlongEdge(1, width, height, 'bottom', gap),
    ];
  }
  if (count === 3) {
    return [
      ...seatsAlongEdge(1, width, height, 'top', gap),
      ...seatsAlongEdge(1, width, height, 'bottom', gap),
      ...seatsAlongEdge(1, width, height, 'right', gap),
    ];
  }
  if (count === 4) {
    return [
      ...seatsAlongEdge(1, width, height, 'top', gap),
      ...seatsAlongEdge(1, width, height, 'right', gap),
      ...seatsAlongEdge(1, width, height, 'bottom', gap),
      ...seatsAlongEdge(1, width, height, 'left', gap),
    ];
  }

  const sides = distributeSides(count, width, height);
  return [
    ...seatsAlongEdge(sides.top, width, height, 'top', gap),
    ...seatsAlongEdge(sides.right, width, height, 'right', gap),
    ...seatsAlongEdge(sides.bottom, width, height, 'bottom', gap),
    ...seatsAlongEdge(sides.left, width, height, 'left', gap),
  ];
};
